import * as THREE from 'three';
import { QualityConfig, QUALITY_CONFIGS } from '../config/quality';
import { store } from '../app/store';

export class EngineRenderer {
  public renderer: THREE.WebGLRenderer;
  public canvas: HTMLCanvasElement;
  private width: number = window.innerWidth;
  private height: number = window.innerHeight;
  private currentQuality: QualityConfig = QUALITY_CONFIGS.medium;
  private resizeHandlers: ((w: number, h: number) => void)[] = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
      stencil: false,
    });

    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.updateSize();
    this.applyQuality(store.getState().quality, store.getState().optimizePerformance);

    window.addEventListener('resize', this.onResize);

    // WebGL Context loss handling
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      console.warn('WebGL Context Lost. Awaiting restoration...');
    });

    canvas.addEventListener('webglcontextrestored', () => {
      console.info('WebGL Context Restored. Re-initializing graphics...');
      this.updateSize();
    });
  }

  private onResize = (): void => {
    this.updateSize();
    for (const h of this.resizeHandlers) {
      h(this.width, this.height);
    }
  };

  public onResizeCallback(handler: (w: number, h: number) => void): () => void {
    this.resizeHandlers.push(handler);
    return () => {
      this.resizeHandlers = this.resizeHandlers.filter(cb => cb !== handler);
    };
  }

  public updateSize(): void {
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    this.renderer.setSize(this.width, this.height);
    this.updatePixelRatio();
  }

  public setPixelRatioScale(scale: number): void {
    const targetDpr = Math.min(window.devicePixelRatio || 1, this.currentQuality.pixelRatioCap) * scale;
    this.renderer.setPixelRatio(Math.max(0.6, targetDpr));
  }

  public updatePixelRatio(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, this.currentQuality.pixelRatioCap);
    this.renderer.setPixelRatio(dpr);
  }

  public applyQuality(preset: string, optimizePerformance: boolean): void {
    const key = (preset === 'auto' ? (optimizePerformance ? 'low' : 'medium') : preset) as keyof typeof QUALITY_CONFIGS;
    this.currentQuality = QUALITY_CONFIGS[key] || QUALITY_CONFIGS.medium;

    if (optimizePerformance) {
      this.currentQuality = { ...this.currentQuality, pixelRatioCap: 1.25, shadows: false };
    }

    this.renderer.shadowMap.enabled = this.currentQuality.shadows;
    this.updatePixelRatio();
  }

  public getWidth(): number {
    return this.width;
  }

  public getHeight(): number {
    return this.height;
  }

  public getAspectRatio(): number {
    return this.width / Math.max(1, this.height);
  }

  public dispose(): void {
    window.removeEventListener('resize', this.onResize);
    this.renderer.dispose();
  }
}
